/**
 * HomePage Component
 * Main page with camera, image capture, and results display
 * 
 * Requirements: 1.1, 1.2, 3.1, 3.3, 3.5
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { CameraManager } from '../services/CameraManager';
import { ImageCaptureService } from '../services/ImageCaptureService';
import { ClassificationClient } from '../services/ClassificationClient';
import { SubscriptionService } from '../services/SubscriptionService';
import { HistoryService } from '../services/HistoryService';
import { ErrorHandler } from '../utils/ErrorHandler';
import { useTutorial, useShouldShowTutorial } from '../components/TutorialProvider';
import VideoDisplay from '../components/VideoDisplay';
import CaptureButton from '../components/CaptureButton';
import SubmitButton from '../components/SubmitButton';
import ResultsDisplay from '../components/ResultsDisplay';
import LoadingSpinner from '../components/LoadingSpinner';
import ProgressIndicator from '../components/ProgressIndicator';
import ClassificationResultOverlay from '../components/ClassificationResultOverlay';
import HistoryThumbnails from '../components/HistoryThumbnails';
import type { ClassificationResult, CameraFacingMode, Subscription } from '../types';
import { CameraDiagnosticsService } from '../utils/CameraDiagnostics';
import './HomePage.css';

export interface HomePageProps {
  creditsRemaining: number | null;
  onCreditsUpdate: () => Promise<void>;
  isThriftLinked?: boolean;
}

function HomePage({ creditsRemaining, onCreditsUpdate, isThriftLinked = false }: HomePageProps) {
  console.log('[HomePage] Component render');
  
  // Tutorial integration
  const { startTutorial } = useTutorial();
  const { shouldShow: shouldShowTutorial, loading: tutorialLoading } = useShouldShowTutorial();
  
  // Services
  const [cameraManager] = useState(() => new CameraManager());
  const [imageCaptureService] = useState(() => new ImageCaptureService());
  const [classificationClient] = useState(() => new ClassificationClient());
  const [subscriptionService] = useState(() => new SubscriptionService());
  const [historyService] = useState(() => new HistoryService());
  const [errorHandler] = useState(() => ErrorHandler.getInstance());

  // State
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [slots, setSlots] = useState(() => imageCaptureService.getSlots());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [latestResult, setLatestResult] = useState<ClassificationResult[]>([]);
  const [showLatestResult, setShowLatestResult] = useState(false);
  const [capturedImageBlobs, setCapturedImageBlobs] = useState<Blob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Subscription state for tier-based features
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [canSeeMultipleItems, setCanSeeMultipleItems] = useState(false);
  const [hasDataDashboardAccess, setHasDataDashboardAccess] = useState(false);
  const [totalItemsDetected, setTotalItemsDetected] = useState(1);

  // History state for thumbnails overlay only
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<ClassificationResult | null>(null);

  // Subscription state removed - now handled in App.tsx

  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const isInitializedRef = useRef(false);
  const inactivityTimerRef = useRef<number | null>(null);

  // Initialize camera on mount
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (isInitializedRef.current) {
      console.log('[Camera] Already initialized, skipping...');
      return;
    }
    
    isInitializedRef.current = true;
    console.log('[Camera] First initialization');

    checkMultipleCameras();
    loadSubscription();

    return () => {
      console.log('[Camera] Cleanup called');
      if (inactivityTimerRef.current !== null) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      cameraManager.release();
    };
  }, []);

  // Tutorial activation for new users
  useEffect(() => {
    // Only start tutorial if:
    // 1. Tutorial loading is complete
    // 2. Tutorial should be shown (user hasn't completed it)
    // 3. Camera is initialized (not initializing)
    // 4. No errors present
    // 5. Not currently running a tutorial (prevent duplicate starts)
    if (!tutorialLoading && shouldShowTutorial && !isInitializing && !error) {
      console.log('[HomePage] Starting tutorial for new user');
      // Small delay to ensure UI is fully rendered and prevent race conditions
      const timer = setTimeout(() => {
        startTutorial('main');
      }, 1500); // Increased delay to prevent conflicts with auth state updates
      
      return () => clearTimeout(timer);
    }
  }, [tutorialLoading, shouldShowTutorial, isInitializing, error, startTutorial]);

  // Load subscription data to determine tier-based features
  const loadSubscription = async () => {
    try {
      const subscriptionData = await subscriptionService.getSubscription();
      setSubscription(subscriptionData);
      
      // Determine if user can see multiple items based on subscription tier
      const tier = subscriptionData.subscription_type.toLowerCase();
      const multipleItemsTiers = ['professional', 'business', 'platinum', 'enterprise'];
      const canSeeMultiple = multipleItemsTiers.includes(tier);
      
      // Determine if user has access to Data Dashboard (Business+)
      const dataDashboardTiers = ['business', 'platinum', 'enterprise'];
      const hasDataAccess = dataDashboardTiers.includes(tier);
      
      setCanSeeMultipleItems(canSeeMultiple);
      setHasDataDashboardAccess(hasDataAccess);
      
      console.log('[HomePage] Subscription loaded:', {
        tier: subscriptionData.subscription_type,
        canSeeMultipleItems: canSeeMultiple,
        hasDataDashboardAccess: hasDataAccess
      });
    } catch (err) {
      console.error('[HomePage] Failed to load subscription:', err);
      // Default to free tier behavior (single item only, no data dashboard)
      setCanSeeMultipleItems(false);
      setHasDataDashboardAccess(false);
    }
  };

  // Credits loading removed - now handled in App.tsx

  // Handle history item click from thumbnails overlay
  // Requirement 8.3: Display detailed results for clicked classification
  const handleHistoryItemClick = (result: ClassificationResult) => {
    setSelectedHistoryItem(result);
  };

  const handleCloseHistoryDetail = () => {
    setSelectedHistoryItem(null);
  };

  // Handle Escape key to close history detail modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedHistoryItem) {
        handleCloseHistoryDetail();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedHistoryItem]);

  const checkMultipleCameras = async () => {
    const hasMultiple = await cameraManager.hasMultipleCameras();
    setHasMultipleCameras(hasMultiple);
  };

  const stopCamera = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    cameraManager.release();
    setStream(null);
    setCameraActive(false);
  }, [cameraManager]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      window.clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = window.setTimeout(stopCamera, 20000);
  }, [stopCamera]);

  // Reset inactivity timer on any user activity while camera is active
  useEffect(() => {
    if (!cameraActive || !stream) return;
    const events = ['mousemove', 'touchstart', 'keydown', 'click'] as const;
    const handleActivity = () => resetInactivityTimer();
    events.forEach((e) => window.addEventListener(e, handleActivity));
    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
    };
  }, [cameraActive, stream, resetInactivityTimer]);

  // Release camera when page is hidden (tab switch / home button)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && cameraActive) {
        stopCamera();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [cameraActive, stopCamera]);

  const initializeCamera = async () => {
    console.log('[Camera] Starting initialization...');
    setIsInitializing(true);
    setError(null);

    try {
      // iOS-specific: Check if we're in a secure context
      if (!window.isSecureContext) {
        throw new Error('Camera access requires HTTPS. Please use a secure connection.');
      }

      // Request camera access
      // Requirement 1.1: Display camera view with live video feed
      console.log('[Camera] Requesting camera access...');
      const mediaStream = await cameraManager.requestCameraAccess('environment');
      console.log('[Camera] Camera access granted, stream:', mediaStream);
      console.log('[Camera] Stream active:', mediaStream.active);
      console.log('[Camera] Video tracks:', mediaStream.getVideoTracks());
      console.log('[Camera] Stream ID:', mediaStream.id);
      
      // Only update stream if it's different
      setStream(prevStream => {
        if (prevStream?.id === mediaStream.id) {
          console.log('[Camera] Stream unchanged, skipping update');
          return prevStream;
        }
        console.log('[Camera] Setting new stream');
        return mediaStream;
      });
    } catch (err) {
      console.error('Camera initialization failed:', err);
      
      // Provide specific error messages for iOS
      let errorMessage = 'Failed to access camera. Please check permissions.';
      if (err instanceof Error) {
        if (err.message.includes('HTTPS')) {
          errorMessage = 'Camera requires HTTPS. Please use a secure connection.';
        } else if (err.message.includes('permission') || err.message.includes('denied')) {
          if (/iPhone|iPad/.test(navigator.userAgent)) {
            errorMessage = 'Camera permission denied. Go to Settings > Safari > Camera (or Chrome > Camera) and enable access, then refresh this page.';
          } else {
            errorMessage = 'Camera permission denied. Please allow camera access and refresh the page.';
          }
        } else if (err.message.includes('not found')) {
          errorMessage = 'No camera found. Please check that your device has a working camera.';
        } else if (err.message.includes('in use')) {
          errorMessage = 'Camera is being used by another app. Please close other camera apps and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      if (err instanceof Error) {
        errorHandler.handleCameraError(err);
      }
    } finally {
      setIsInitializing(false);
      console.log('[Camera] Initialization complete');
    }
  };

  const startCamera = async () => {
    setCameraActive(true);
    await initializeCamera();
    resetInactivityTimer();
  };

  const handleVideoReady = useCallback((videoElement: HTMLVideoElement) => {
    // Prevent multiple calls for the same video element
    if (videoElementRef.current === videoElement) {
      console.log('[Camera] Video element already set, skipping');
      return;
    }
    
    console.log('[Camera] Video element ready:', videoElement);
    console.log('[Camera] Video srcObject:', videoElement.srcObject);
    console.log('[Camera] Video dimensions:', videoElement.videoWidth, 'x', videoElement.videoHeight);
    videoElementRef.current = videoElement;
    cameraManager.setVideoElement(videoElement);
  }, [cameraManager]);

  const handleCapture = async () => {
    if (!videoElementRef.current) {
      setError('Video not ready. Please wait.');
      return;
    }

    try {
      // Requirement 1.2: Capture current camera frame to image slot
      const capturedSlot = await imageCaptureService.captureToSlot(videoElementRef.current);
      
      // Update slots state
      setSlots(imageCaptureService.getSlots());
      setError(null);

      console.log('Image captured successfully:', capturedSlot.id);
    } catch (err) {
      console.error('Capture failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to capture image');
      errorHandler.handleError(err as Error, { component: 'capture' });
    }
  };

  const handleRemove = (slotId: string) => {
    // Requirement 2.3: Clear image slot
    imageCaptureService.clearSlot(slotId);
    setSlots(imageCaptureService.getSlots());
    setError(null);
  };

  const handleSubmit = async () => {
    const filledSlots = imageCaptureService.getFilledSlots();
    
    if (filledSlots.length === 0) {
      setError('No images to submit');
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      console.log('[HomePage] Submission already in progress, ignoring');
      return;
    }

    setIsSubmitting(true);
    setSubmissionProgress(0);
    setError(null);
    
    // Clear any existing progress interval
    let progressInterval: number | null = null;

    try {
      // Start smooth animated progress bar
      let currentProgress = 0;
      progressInterval = window.setInterval(() => {
        if (currentProgress < 99) {
          // Random increment between 1-5% with variable speed
          const increment = Math.random() * 4 + 1;
          currentProgress = Math.min(currentProgress + increment, 99);
          setSubmissionProgress(currentProgress);
        }
      }, 200 + Math.random() * 300); // Random interval between 200-500ms

      // Requirement 3.1: Send classification request for all captured images in one batch
      const frames = filledSlots
        .filter(slot => slot.blob !== null)
        .map(slot => ({
          blob: slot.blob!,
          frameId: `${slot.id}-${slot.timestamp}`
        }));

      if (frames.length === 0) {
        throw new Error('No valid images to submit');
      }

      console.log(`[HomePage] Submitting ${frames.length} images for classification`);

      // Upload all frames in a single request
      // Credit consumption: 1 image = 1 credit, 2-3 images = 2 credits
      const classificationResults = await classificationClient.uploadFrames(frames);
      
      console.log('[HomePage] Received classification results:', {
        totalResults: classificationResults.length,
        canSeeMultipleItems,
        subscription: subscription?.subscription_type,
        results: classificationResults.map(r => ({ frame_id: r.frame_id, description: r.description }))
      });
      
      // Stop animated progress and complete to 100%
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setSubmissionProgress(100);

      // Small delay to show 100% completion
      await new Promise(resolve => setTimeout(resolve, 500));

      // Requirement 3.3: Display results based on subscription tier
      // Professional+ tiers see all items, Free/Starter see only first item
      const displayResults = canSeeMultipleItems ? classificationResults : classificationResults.slice(0, 1);
      
      console.log('[HomePage] Filtered results for display:', {
        originalCount: classificationResults.length,
        displayCount: displayResults.length,
        canSeeMultipleItems,
        tier: subscription?.subscription_type
      });
      
      setTotalItemsDetected(classificationResults.length);

      // Preserve all image blobs so the THRIFT push button can send them.
      // Must happen before clearAllSlots() below.
      setCapturedImageBlobs(frames.map(f => f.blob));

      // Show all results in overlay based on subscription tier
      if (classificationResults.length > 0) {
        // Pass the filtered results to overlay (all for Professional+, first only for Free/Starter)
        setLatestResult(displayResults);
        setShowLatestResult(true);
        
        // Log tier-based filtering for debugging
        if (!canSeeMultipleItems && classificationResults.length > 1) {
          console.log(`[HomePage] Tier restriction: Showing 1 of ${classificationResults.length} detected items. Upgrade to Professional+ to see all items.`);
        }
        
        // Log what will be displayed in overlay
        console.log(`[HomePage] Overlay will show ${displayResults.length} items:`, 
          displayResults.map(r => r.description));
      } else {
        // No garments found in any of the images
        setError('No garments detected in the submitted images. Please capture images containing clothing or textile items.');
      }

      // Requirement 3.5: Clear all image slots after successful classification
      imageCaptureService.clearAllSlots();
      setSlots(imageCaptureService.getSlots());

      // Refresh credits counter
      console.log('[HomePage] Updating credits after successful classification...');
      await onCreditsUpdate();
      console.log('[HomePage] Credits updated');

      const creditsUsed = frames.length === 1 ? 1 : 2;
      console.log(`[HomePage] Successfully classified ${classificationResults.length} images using ${creditsUsed} credit(s)`);
    } catch (err) {
      // Stop animated progress on error
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      
      console.error('Classification failed:', err);
      
      // Handle specific error types
      let errorMessage = 'Classification failed';
      if (err instanceof Error) {
        if (err.message.includes('Network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (err.message.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      // Requirement 3.4: Display error message with details
      setError(errorMessage);
      errorHandler.handleError(err as Error, { component: 'classification' });
    } finally {
      // Ensure cleanup happens
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setIsSubmitting(false);
      setSubmissionProgress(0);
    }
  };

  const handleSwitchCamera = async () => {
    try {
      const currentMode = cameraManager.getCurrentFacingMode();
      const newMode: CameraFacingMode = currentMode === 'environment' ? 'user' : 'environment';

      await cameraManager.switchCamera(newMode);
      const newStream = cameraManager.getStream();
      setStream(newStream);
    } catch (err) {
      console.error('Camera switch failed:', err);
      setError('Failed to switch camera');
      errorHandler.handleError(err as Error, { component: 'camera-switch' });
    }
  };

  const handleSaveFeedback = useCallback(async (frameId: string, feedback: string) => {
    await historyService.saveFeedback(frameId, feedback);
    setLatestResult((prev) =>
      prev.map((r) => r.frame_id === frameId ? { ...r, user_feedback: feedback } : r)
    );
    setSelectedHistoryItem((prev) =>
      prev?.frame_id === frameId ? { ...prev, user_feedback: feedback } : prev
    );
  }, [historyService]);

  const handleSaveCriterionVote = useCallback(async (
    frameId: string,
    criterionName: string,
    vote: 'up' | 'down'
  ) => {
    await historyService.saveCriterionVote(frameId, criterionName, vote);
    const applyVote = (r: import('../types').ClassificationResult) =>
      r.frame_id !== frameId ? r : {
        ...r,
        criteria_results: r.criteria_results.map((cr) =>
          cr.criterion_name === criterionName ? { ...cr, vote } : cr
        ),
      };
    setLatestResult((prev) => prev.map(applyVote));
    setSelectedHistoryItem((prev) => prev ? applyVote(prev) : prev);
  }, [historyService]);

  const handleCloseLatestResult = async () => {
    console.log('[HomePage] Closing result overlay, refreshing credits...');
    setShowLatestResult(false);
    setCapturedImageBlobs([]);
    
    // Refresh credits when closing result overlay to ensure count is up-to-date
    await onCreditsUpdate();
    console.log('[HomePage] Credits refreshed after closing overlay');
  };

  // Debug function for camera diagnostics
  const handleCameraDiagnostics = async () => {
    try {
      const report = await CameraDiagnosticsService.getDiagnosticReport();
      console.log('Camera Diagnostics Report:\n', report);
      
      // Also test camera access
      const testResult = await CameraDiagnosticsService.testCameraAccess();
      console.log('Camera Test Result:', testResult);
      
      // Show in alert for easy copying
      alert(`Camera Diagnostics:\n\n${report}\n\nTest Result: ${testResult.success ? 'SUCCESS' : `FAILED - ${testResult.error}`}`);
      
      // Clean up test stream if created
      if (testResult.stream) {
        testResult.stream.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      console.error('Diagnostics failed:', error);
      alert(`Diagnostics failed: ${error}`);
    }
  };

  const isCaptureDisabled = !stream || isInitializing || imageCaptureService.isFull();
  const filledCount = imageCaptureService.getFilledCount();
  
  // Calculate credits needed: 1 image = 1 credit, 2-3 images = 2 credits
  const creditsNeeded = filledCount === 1 ? 1 : filledCount >= 2 ? 2 : 0;
  const hasInsufficientCredits = creditsRemaining !== null && creditsRemaining < creditsNeeded;
  
  const isSubmitDisabled = !imageCaptureService.hasImages() || isSubmitting || hasInsufficientCredits;

  return (
    <div className="home-page">
      {/* Full-Screen Camera View */}
      <div className="camera-viewport">
        {!cameraActive ? (
          <div className="camera-start-screen">
            <div className="camera-start-content">
              <button className="camera-start-btn" onClick={startCamera}>
                Start
              </button>
            </div>
          </div>
        ) : isInitializing ? (
          <div className="camera-loading-fullscreen">
            <LoadingSpinner size="large" message="Initializing camera..." />
          </div>
        ) : stream ? (
          <VideoDisplay
            stream={stream}
            onVideoReady={handleVideoReady}
            className="camera-video-fullscreen"
          />
        ) : (
          <div className="camera-loading-fullscreen">
            <div style={{ textAlign: 'center', color: '#fff' }}>
              <p>Camera not available</p>
              <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.5rem' }}>
                Please allow camera access or check if camera is available
              </p>
              <button
                onClick={startCamera}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#4a90e2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Retry Camera Access
              </button>
              {/* Debug diagnostics button - only in development */}
              {import.meta.env.DEV && (
                <button
                  onClick={handleCameraDiagnostics}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Camera Diagnostics
                </button>
              )}
            </div>
          </div>
        )}

        {/* Camera Controls Overlay - Top */}
        {cameraActive && (
          <div className="camera-controls-top">
            {hasMultipleCameras && (
              <button
                className="camera-switch-fab"
                onClick={handleSwitchCamera}
                disabled={isInitializing}
                aria-label="Switch camera"
                title="Switch camera"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Captured Images Overlay - Bottom Right */}
        {cameraActive && (
          <div className="captured-images-overlay">
            <div className="overlay-header">
              <span className="overlay-count">{filledCount}/3</span>
            </div>
            <div className="overlay-thumbnails">
              {slots.map((slot) => (
                <div
                  key={slot.id}
                  className={`overlay-thumbnail ${slot.dataUrl ? 'filled' : 'empty'}`}
                >
                  {slot.dataUrl ? (
                    <>
                      <img src={slot.dataUrl} alt={`Captured ${slot.id}`} />
                      <button
                        className="overlay-remove"
                        onClick={() => handleRemove(slot.id)}
                        aria-label={`Remove image ${slot.id}`}
                        title="Remove image"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <div className="overlay-empty-slot">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons Overlay - Bottom Center */}
        {cameraActive && (
          <div className="camera-actions-bottom">
            <CaptureButton disabled={isCaptureDisabled} onClick={handleCapture} className="capture-button" />
            <SubmitButton
              disabled={isSubmitDisabled}
              isSubmitting={isSubmitting}
              imageCount={filledCount}
              onClick={handleSubmit}
              insufficientCredits={hasInsufficientCredits}
              creditsNeeded={creditsNeeded}
              className="submit-button"
            />
          </div>
        )}

        {/* Progress Indicator Overlay */}
        {isSubmitting && (
          <div className="progress-overlay" role="status" aria-live="polite" aria-atomic="true">
            <div className="progress-content">
              <LoadingSpinner size="large" />
              <ProgressIndicator
                progress={submissionProgress}
                message={`Processing ${filledCount} image${filledCount > 1 ? 's' : ''}...`}
                showPercentage={true}
                variant="linear"
                size="medium"
              />
            </div>
          </div>
        )}

        {/* Error Toast Overlay */}
        {error && (
          <div className="error-toast">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button
              className="error-dismiss"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        {/* Latest Classification Result Overlay */}
        {showLatestResult && latestResult.length > 0 && (
          <ClassificationResultOverlay
            results={latestResult}
            onClose={handleCloseLatestResult}
            subscription={subscription}
            totalItemsDetected={totalItemsDetected}
            canSeeMultipleItems={canSeeMultipleItems}
            className="classification-result-overlay"
            onSaveFeedback={handleSaveFeedback}
            onSaveCriterionVote={handleSaveCriterionVote}
            isThriftLinked={isThriftLinked}
            imageBlobs={capturedImageBlobs}
          />
        )}

        {/* History Thumbnails Overlay - Business+ only */}
        {hasDataDashboardAccess && (
          <HistoryThumbnails
            onItemClick={handleHistoryItemClick}
            className="history-thumbnails"
          />
        )}
      </div>

      {/* History Detail Modal */}
      {/* Requirement 8.3: Display detailed results for clicked item from thumbnails */}
      {selectedHistoryItem && (
        <div 
          className="history-detail-modal" 
          onClick={handleCloseHistoryDetail}
          role="dialog"
          aria-modal="true"
          aria-labelledby="history-detail-title"
        >
          <div className="history-detail-content" onClick={(e) => e.stopPropagation()}>
            <div className="history-detail-header">
              <h2 id="history-detail-title">Classification Details</h2>
              <button
                className="history-detail-close"
                onClick={handleCloseHistoryDetail}
                aria-label="Close details"
              >
                ×
              </button>
            </div>
            <div className="history-detail-body">
              <ResultsDisplay result={selectedHistoryItem} disableTooltips={true} onSaveFeedback={handleSaveFeedback} subscription={subscription} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
